import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useBrainData(token) {
  const [overview, setOverview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [brainSources, setBrainSources] = useState([]);
  const [taskQueue, setTaskQueue] = useState(null);
  const [topContacts, setTopContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchData = useCallback(async () => {
    try {
      const [ovRes, tlRes, srcRes] = await Promise.all([
        axios.get(`${API}/dashboard/overview?days=30`, { headers }),
        axios.get(`${API}/dashboard/activity-timeline?days=30`, { headers }),
        axios.get(`${API}/dashboard/brain-sources`, { headers }),
      ]);
      setOverview(ovRes.data);
      setTimeline(tlRes.data.timeline || []);
      setBrainSources(srcRes.data.sources || []);

      // Fetch task queue from diag (lighter than individual calls)
      try {
        const diagRes = await axios.get(`${API}/dashboard/task-stats`, { headers });
        setTaskQueue(diagRes.data || null);
      } catch {
        setTaskQueue(null);
      }

      // Top contacts by activity
      try {
        const cRes = await axios.get(`${API}/contacts?limit=30&sort=-last_activity`, { headers });
        setTopContacts((cRes.data.contacts || cRes.data || []).slice(0, 30));
      } catch {
        setTopContacts([]);
      }
    } catch (e) {
      console.error('Brain data fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Derived metrics
  const metrics = useMemo(() => {
    if (!overview) return null;
    const totalMemories = overview.total_memories || 0;
    const totalContacts = overview.active_contacts || 0;
    const todayTotal = overview.today?.total || 0;
    const byType = overview.by_type || {};

    // Activity intensity (0-1) based on today vs average
    const avgDaily = totalMemories / 30;
    const intensity = avgDaily > 0 ? Math.min(todayTotal / (avgDaily * 2), 1) : 0.1;

    // Dominant source
    const typeValues = Object.entries(byType).sort((a, b) => (b[1]?.count || b[1]) - (a[1]?.count || a[1]));
    const dominant = typeValues[0]?.[0] || 'call';

    // Worker health
    const queueHealth = taskQueue
      ? (taskQueue.failed_24h === 0 ? 1 : Math.max(0, 1 - taskQueue.failed_24h / 10))
      : 0.8;

    return {
      totalMemories,
      totalContacts,
      todayTotal,
      intensity,
      dominant,
      queueHealth,
      sourceList: brainSources,
      completedTasks: taskQueue?.completed_24h || 0,
      failedTasks: taskQueue?.failed_24h || 0,
      pendingTasks: taskQueue?.pending || 0,
      byType,
    };
  }, [overview, taskQueue, brainSources]);

  return { metrics, timeline, topContacts, loading, refetch: fetchData };
}
